import * as React from 'react';
import * as D3Array from 'd3-array';
import * as moment from 'moment';
import { formatNumeral } from '@utils/helpers';

const { LineChart } = require( 'kaidoo-chart/basic' );
import { DateInterpolator } from './lib/interpolator';
import { XDomain, YDomain } from '../../widgets/performanceOverview';
import type { PerformanceDetailChartPropsType, PossibleSteps } from './types';

const dateInterpolator = new DateInterpolator();

const yAxisFormatFuncPlus = ( useLocale, d ) => {
    const locale = ( useLocale ? useLocale : 'de' ).split( '-' )[ 0 ];
    return formatNumeral( ( d - 100 ) / 100, '0,0.00%', locale );
};

const makeYTicks = ( detailYDomain: YDomain, yDomain: YDomain, invert ) => {
    const [ detailYDomainMin, detailYDomainMax ] = D3Array.extent<number>( detailYDomain ) as YDomain;

    const count = 5;
    const roundedDeltaY = Number.parseFloat(
        ( ( detailYDomainMax - detailYDomainMin ) / ( count - 1 ) ).toPrecision( 1 )
    );

    const result = [
        ...D3Array.range( 100, detailYDomainMin, - roundedDeltaY ).reverse().slice( 1 ),
        ...D3Array.range( 100, detailYDomainMax, roundedDeltaY ),
    ].sort();

    if ( Math.abs( result[ result.length - 1 ] - detailYDomainMax ) >= 0.3 * roundedDeltaY ) {
        result.push( detailYDomainMax );
    }

    if ( Math.abs( result[ 0 ] - detailYDomainMin ) >= 0.3 * roundedDeltaY ) {
        result.push( detailYDomainMin );
    }

    if ( invert ) {
        result.reverse();
    }

    return result;
};

const makeXTicks = ( xDomain: XDomain, xScale ) => {
    const [ xDomainMin, xDomainMax ] = D3Array.extent( xDomain );
    const minDelta = ( xScale.invert( Math.min( ...xScale.range() ) + 75 ) -
        xScale.invert( Math.min( ...xScale.range() ) ) );

    const deltaXDays = Math.ceil(
        Math.abs( xDomainMax.valueOf() - minDelta  - xDomainMin.valueOf() ) / ( 1000 * 3600 * 24 )
    );

    const year = 365;
    const twoyears = year * 2;
    const threeyears = year * 3;
    const fouryears = year * 4;
    const fiveyears = year * 5;
    const sixyears = year * 6;
    const sevenyears = year * 7;
    const eightyears = year * 8;
    const nineyears = year * 9;
    const tenyears = year * 10;
    const elevenyears = year * 11;
    const twelveyears = year * 12;
    const thirteenyears = year * 13;
    const fourteenyears = year * 14;
    const fifteenyears = year * 15;
    const sixteenyears = year * 16;
    const seventeenyears = year * 17;
    const eighteenyears = year * 18;
    const nineteenyears = year * 19;
    const twentyyears = year * 20;
    const threequarters = 270;
    const halfyear = year / 2;
    const quartal = 90;
    const twomonths = 60;
    const month = 30;
    const threeweeks = 21;
    const twoweeks = 14;
    const week = 7;
    const fourdays = 4;
    const threedays = 3;
    const twodays = 2;
    const day = 1;

    const allSteps = {
        twentyyears,
        nineteenyears,
        eighteenyears,
        seventeenyears,
        sixteenyears,
        fifteenyears,
        fourteenyears,
        thirteenyears,
        twelveyears,
        elevenyears,
        tenyears,
        nineyears,
        eightyears,
        sevenyears,
        sixyears,
        fiveyears,
        fouryears,
        threeyears,
        twoyears,
        year,
        threequarters,
        halfyear,
        quartal,
        twomonths,
        month,
        threeweeks,
        twoweeks,
        week,
        fourdays,
        threedays,
        twodays,
        day,
    };

    const possibleSteps: PossibleSteps = Object.values( allSteps ).reduce<any>( ( prev, curr, ind ) => {
        if ( curr >= Math.ceil( minDelta / ( 1000 * 3600 * 24 ) ) ) {
            if ( deltaXDays / curr >= 2 ) {
                prev[Object.keys( allSteps )[ind]] = curr;
            }
            else if ( ( deltaXDays / curr >= 1 ) && minDelta <= 1000 * 3600 * 24 * 7 ) {
                prev[Object.keys( allSteps )[ind]] = curr;
            }
        }
        return prev;
    }, {} );

    const xAxisTicks = [ xDomainMin ];

    if ( Object.keys( possibleSteps ).length === 0 ) {
        xAxisTicks.push( xDomainMax );
        return [ ...xAxisTicks, xDomainMax ];
    }

    const minLastStepModIndex = Object.values( possibleSteps ).indexOf( Math.max( ...Object.values( possibleSteps ) ) );

    const chosenStepString = Object.keys( possibleSteps )[minLastStepModIndex];
    const chosenStep = Object.values( possibleSteps )[minLastStepModIndex] as number;

    const xTickCount = Math.floor( deltaXDays / chosenStep );

    const translateStepString = {
        twentyyears: [ 20, 'years' ],
        nineteenyears: [ 19, 'years' ],
        eighteenyears: [ 18, 'years' ],
        seventeenyears: [ 17, 'years' ],
        sixteenyears: [ 16, 'years' ],
        fifteenyears: [ 15, 'years' ],
        fourteenyears: [ 14, 'years' ],
        thirteenyears: [ 13, 'years' ],
        twelveyears: [ 12, 'years' ],
        elevenyears: [ 11, 'years' ],
        tenyears: [ 10, 'years' ],
        nineyears: [ 9, 'years' ],
        eightyears: [ 8, 'years' ],
        sevenyears: [ 7, 'years' ],
        sixyears: [ 6, 'years' ],
        fiveyears: [ 5, 'years' ],
        fouryears: [ 4, 'years' ],
        threeyears: [ 3, 'years' ],
        twoyears: [ 2, 'years' ],
        year: [ 1, 'years' ],
        threequarters: [ 9, 'months' ],
        halfyear: [ 6, 'months' ],
        quartal: [ 3, 'months' ],
        twomonths: [ 2, 'months' ],
        month: [ 1, 'months' ],
        threeweeks: [ 3, 'weeks' ],
        twoweeks: [ 2, 'weeks' ],
        week: [ 1, 'weeks' ],
        fourdays: [ 4, 'days' ],
        threedays: [ 3, 'days' ],
        twodays: [ 2, 'days' ],
        day: [ 1, 'days' ],
    };
    for ( let i = 0; i < xTickCount; i++ ) {
        const chosenStepArray = translateStepString[ chosenStepString ];
        if ( chosenStepArray ) {
            const parsedZone = moment.parseZone( xAxisTicks[ xAxisTicks.length - 1 ] );
            xAxisTicks.push( parsedZone.add.apply( parsedZone, chosenStepArray ).toDate() );
        }
    }

    return [ ...xAxisTicks, xDomainMax ];
};

const PerformanceDetailChart: React.FC<PerformanceDetailChartPropsType> = ( props ) => {
    const rawData = props.data;

    dateInterpolator.setReference();
    let data = dateInterpolator.interpolate( rawData );

    if ( props.hasBenchmarkData ) {
        dateInterpolator.switchReference();
        data = dateInterpolator.interpolate( data );
    }

    const { timeFormatter } = props;
    const yAxisHShift = -110;
    const xAxisVShift = -35;

    const { xDomain, yDomain, xRange, width, height, margins } = props;
    const yRange = [ height - margins.top - margins.bottom, 0 ];

    const x = ( d ) => {
        return d.date;
    };

    const yGutmannPlus = ( d ) => {
        return d;
    };

    const legendConfig = {
        show: false,
        backgroundColor: '#FFF',
        legendHeight: 50,
        legendPosition: 'left',
        legendOffset: 90,
        legendClassName: 'react-d3-core__legend',
        swatchShape: 'square',
    };

    const dataSeriesConfig = props.hasBenchmarkData ? [
        {
            field: 'performance',
            name: 'Performance',
            className: 'performance',
        },
        {
            field: 'benchmark',
            name: 'Benchmark',
            className: 'benchmark',
        },
    ] : [
        {
            field: 'performance',
            name: 'Performance',
            className: 'performance',
        },
    ];

    const xScaleSetConfig = [
        {
            scale: 'time',
            valueFunc: x,
            domain: xDomain,
            range: [ xRange[0], xRange[1] - Math.abs( yAxisHShift ) ],
            axis: {
                show: true,
                orient: 'bottom',
                className: 'xAxis',
                hShift: 0,
                vShift: -xAxisVShift,
                label: {
                    show: true,
                    title: '',
                    position: 'top',
                    offset: 0,
                    hShift: 0,
                    vShift: 0,
                    rotate: 0,
                    customStyle: null,
                    className: '',
                },
                ticks: {
                    sizeInner: 5,
                    sizeOuter: 5,
                    values: makeXTicks.bind( this, xDomain ),
                    format: timeFormatter,
                    count: null,
                    padding: 4,
                    orient: 'top',
                    customTickStyle: null,
                    tickClassName: 'xAxisTicks',
                    customTickLabelStyle: null,
                    tickLabelClassName: 'xAxisTickLabel',
                },
            },
            grid: {
                show: false,
                borderExcess: [ 0, 0 ],
                shift: 0,
                className: 'xGridLine',
            },
        },
    ];

    const borderExcess = [ 2, 2 ];
    const yGridDashFunction = ( xScale, yScale ) => {
        const xTicks = makeXTicks( xDomain, xScale );
        return xTicks.sort( ( a: Date, b: Date ) => a.valueOf() - b.valueOf() ).map( ( date, i ) => {
            const dashLength = 4;
            if ( i === 0 ){
                return (
                    [ 0, ( xScale( date ) - xScale( xDomain[0] ) - dashLength * 0.5 + borderExcess[0] ), dashLength ]
                );
            }

            if ( i === ( xTicks.length - 1 ) ){
                return (
                    [ Math.max( 0, xScale( date ) - xScale( xTicks[i - 1] ) - dashLength ), dashLength, margins.right ]
                );
            }

            return (
                [ ( xScale( date ) - xScale( xTicks[i - 1] ) ) - dashLength, dashLength ]
            );

        } ).reduce( ( a, b ) => {
            return a.concat( b );
        }, [] ).toString();
    };

    const customStyleFunc = ( xScale, yScale ) => {
        const yGridDashes = yGridDashFunction( xScale, yScale );
        return (
            {
                strokeDasharray: yGridDashes,
            }
        );
    };

    const yScaleSetConfig = [
        {
            scale: 'log',
            base: 10,
            valueFunc: yGutmannPlus,
            domain: yDomain,
            range: yRange,
            axis: {
                show: true,
                orient: 'right',
                customStyle: null,
                className: 'yAxis',
                hShift: -2,
                vShift: 0,
                label: {
                    show: false,
                    title: '',
                    position: 'right',
                    offset: 0,
                    hShift: 0,
                    vShift: 0,
                    rotate: 0,
                    customStyle: null,
                    className: '',
                },
                ticks: {
                    sizeInner: 15.0,
                    sizeOuter: 0.0,
                    values: makeYTicks( yDomain, yDomain, false ),
                    format: yAxisFormatFuncPlus.bind( null, props.language ),
                    count: null,
                    padding: 2,
                    orient: 'left',
                    customTickStyle: null,
                    tickClassName: 'yAxisTicks',
                    customTickLabelStyle: null,
                    tickLabelClassName: 'yAxisTickLabel',
                },
            },
            grid: {
                show: true,
                borderExcess,
                shift: 0,
                className: 'yGridLinePlus',
                customStyle: customStyleFunc,
            },
        },
    ];

    return (
        <LineChart
            width={ width }
            height={ height }
            data={ data }
            dataSeriesConfig={ dataSeriesConfig }
            margins={ margins }

            xScaleSetConfig={ xScaleSetConfig }
            yScaleSetConfig={ yScaleSetConfig }

            legendConfig={ legendConfig }
        />
    );
};

export { PerformanceDetailChart };
